import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  message,
  Space,
  Alert
} from 'antd';
import {
  UserOutlined,
  LockOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  TeamOutlined,
  CrownOutlined
} from '@ant-design/icons';
import {
  updateUser,
  clearError,
  clearSuccess
} from '../../features/account/userManagementSlice';

const EditUser = ({ visible, onCancel, onSuccess, user }) => {
  const dispatch = useDispatch();
  const { loading, error, success, operationType } = useSelector(
    (state) => state.userManagement
  );
  
  const [form] = Form.useForm();

  useEffect(() => {
    if (user && visible) {
      form.setFieldsValue({
        name: user.name,
        role: user.role,
        password: '' // Always start with empty password
      });
    }
  }, [user, visible, form]);

  useEffect(() => {
    if (success && operationType === 'update') {
      message.success('User updated successfully!');
      dispatch(clearSuccess());
      form.resetFields();
      onSuccess && onSuccess();
    }
  }, [success, operationType, dispatch, form, onSuccess]);

  useEffect(() => {
    if (error && operationType === 'update') {
      message.error(error);
      dispatch(clearError());
    }
  }, [error, operationType, dispatch]);

  const handleSubmit = async (values) => {
    try {
      // Only send fields that have values or have changed
      const updateData = {};
      
      if (values.name && values.name.trim() !== '' && values.name.trim() !== user.name) {
        updateData.name = values.name.trim();
      }
      
      if (values.role && values.role !== user.role) {
        updateData.role = values.role;
      }
      
      if (values.password && values.password.trim() !== '') {
        updateData.password = values.password;
      }

      // If no fields to update, show message
      if (Object.keys(updateData).length === 0) {
        message.warning('No changes detected. Please modify at least one field to update.');
        return;
      }

      await dispatch(updateUser({ 
        userId: user._id, 
        userData: updateData 
      })).unwrap();
    } catch (error) {
      // Error is handled by useEffect
    }
  };

  const handleCancel = () => {
    form.resetFields();
    dispatch(clearError());
    onCancel && onCancel();
  };

  if (!user) return null;

  return (
    <Modal
      title={
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
            <UserOutlined className="text-white text-sm" />
          </div>
          <span>Edit User</span>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
      destroyOnClose
    >
      <div className="mb-4">
        <Alert
          message={`Editing User: ${user.name}`}
          description={
            <div>
              <p><strong>Email:</strong> {user.email} (cannot be changed)</p>
              <p><strong>Current Role:</strong> {user.role.toUpperCase()}</p>
              <p className="mt-2 text-sm">You can update the user's name, role, and/or set a new password. Leave password empty if you don't want to change it.</p>
            </div>
          }
          type="info"
          showIcon
        />
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        size="large"
        className="mt-4"
      >
        <Form.Item
          label="User Role"
          name="role"
          rules={[
            { required: true, message: 'Please select a user role' }
          ]}
          extra="Select the appropriate role for this user"
        >
          <Select
            placeholder="Select user role"
            className="rounded-lg"
          >
            <Select.Option value="admin">
              <div className="flex items-center space-x-2">
                <TeamOutlined />
                <span>Admin - Full system access</span>
              </div>
            </Select.Option>
            <Select.Option value="omega">
              <div className="flex items-center space-x-2 text-purple-600">
                <CrownOutlined />
                <span>Omega - Access to Creond 🔒</span>
              </div>
            </Select.Option>
            <Select.Option value="devuser">
              <div className="flex items-center space-x-2">
                <UserOutlined />
                <span>Developer User - Development & Media access</span>
              </div>
            </Select.Option>
            <Select.Option value="desuser">
              <div className="flex items-center space-x-2">
                <UserOutlined />
                <span>Designer User - Design & Media access</span>
              </div>
            </Select.Option>
            <Select.Option value="prouser">
              <div className="flex items-center space-x-2">
                <UserOutlined />
                <span>Pro User - All features except Account</span>
              </div>
            </Select.Option>
            <Select.Option value="visitor">
              <div className="flex items-center space-x-2">
                <UserOutlined />
                <span>Visitor - Basic access only</span>
              </div>
            </Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Full Name"
          name="name"
          rules={[
            { min: 2, message: 'Name must be at least 2 characters long' },
            { max: 50, message: 'Name cannot exceed 50 characters' }
          ]}
        >
          <Input
            prefix={<UserOutlined className="text-gray-400" />}
            placeholder="Enter user's full name"
            className="rounded-lg"
          />
        </Form.Item>

        <Form.Item
          label="New Password (Optional)"
          name="password"
          rules={[
            { min: 6, message: 'Password must be at least 6 characters long' },
            { 
              pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
              message: 'Password must contain at least one uppercase letter, one lowercase letter, and one number'
            }
          ]}
          extra="Leave empty if you don't want to change the password"
        >
          <Input.Password
            prefix={<LockOutlined className="text-gray-400" />}
            placeholder="Enter new password (optional)"
            className="rounded-lg"
            iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
          />
        </Form.Item>

        <Form.Item className="mb-0 pt-4">
          <Space className="w-full justify-end">
            <Button 
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<UserOutlined />}
              className="bg-blue-600 hover:bg-blue-700 border-blue-600 hover:border-blue-700"
            >
              {loading ? 'Updating User...' : 'Update User'}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditUser;